import { store } from "@store";
import { observable, action } from "mobx";
import { ObservableQuery } from "@apollo/client";
import { CustomerContracts, CustomerContractsTotal } from "@graphql";
import { InfiniteScrollSearchManager } from "@search/InfiniteScrollSearchManager";
import { CustomerContractsFilterManager } from "./CustomerContractsFilterManager";
import { CustomerContractOption, initialFilter, Row, sortOptionToGql } from "./utils";

import { logSubscriptionError } from "@utils/log-subscription-error";
export class CustomerContractsSearchStore {
  searchManager: InfiniteScrollSearchManager<Row>;
  filterManager: CustomerContractsFilterManager;
  query: ObservableQuery<CustomerContracts.Return, CustomerContracts.Variables>;
  queryTotal: ObservableQuery<
    CustomerContractsTotal.Return,
    CustomerContractsTotal.Variables
  >;

  @observable hasMore = true;
  @observable selectedSortOption: CustomerContractOption = "customer_name_asc";
  @observable total: number | null = null;
  @observable totalLoading = false;

  constructor() {
    this.fetch = this.fetch.bind(this);
    this.filterManager = new CustomerContractsFilterManager(
      { initialFilter },
      async () => {
        await this.fetch();
        this.refreshTotal();
      }
    );
    this.searchManager = new InfiniteScrollSearchManager<Row>(this.fetch, true);
    this.searchManager.fetch();

    (window as any).customerContractSearchStore = this;
  }

  init() {
    const sort = sortOptionToGql(this.selectedSortOption);
    this.query = CustomerContracts.funcWatch(
      {
        filter: this.filterManager.filter,
        limit: 20,
        skip: 0,
        sortBy: sort.sortBy,
        sortOrder: sort.sortOrder
      },
      { fetchPolicy: "network-only" }
    );

    this.queryTotal = CustomerContractsTotal.funcWatch(
      {
        filter: this.filterManager.filter
      },
      { fetchPolicy: "network-only" }
    );

    this.subscribe();
  }

  subscribe() {
    this.query.subscribe({
      next: ({ data }) => {
        if (!data.customerContracts.items.length) {
          this.hasMore = false;
        }
        this.searchManager.merge(data.customerContracts.items);
      },
      error: logSubscriptionError("CustomerContractsSearchStore")
    });

    this.queryTotal.subscribe({
      next: ({ data, loading }) => {
        this.totalLoading = loading;
        if (!loading && data) {
          this.total = data.customerContractsTotal.total;
        }
      },
      error: logSubscriptionError("CustomerContractsSearchStore")
    });
  }

  fetch(skip?: number, limit?: number) {
    const _skip = skip == null ? 0 : skip;
    const _limit = limit == null ? 20 : limit;

    const isNew = !_skip;

    isNew && this.searchManager.clear();

    if (isNew) {
      this.hasMore = true;
      this.init();
      return Promise.resolve();
    } else {
      const sort = sortOptionToGql(this.selectedSortOption);

      return this.query.fetchMore({
        variables: {
          filter: this.filterManager.filter,
          skip: _skip,
          limit: _limit,
          sortBy: sort.sortBy,
          sortOrder: sort.sortOrder
        },
        updateQuery: (previousQueryResult, options) => {
          if (!options.fetchMoreResult.customerContracts.items.length) {
            this.hasMore = false;
          }
          return {
            ...previousQueryResult,
            customerContracts: {
              ...previousQueryResult.customerContracts,
              items: [
                ...previousQueryResult.customerContracts.items,
                ...options.fetchMoreResult.customerContracts.items
              ]
            }
          };
        }
      });
    }
  }

  // Method to refresh total count when filters change
  @action refreshTotal() {
    if (this.queryTotal) {
      this.totalLoading = true;
      this.queryTotal.refetch({
        filter: this.filterManager.filter
      });
    }
  }

  // Getter for total count
  get totalCount(): number | null {
    return this.total;
  }

  // Getter for total loading state
  get isTotalLoading(): boolean {
    return this.totalLoading;
  }

  @action.bound handleUploadFile() {
    store()
      .upload.requestFileUpload({ multiple: true })
      .then((files) => {
        this.addUploadFiles(files);
      });
  }

  @action.bound addUploadFiles(files: File[]) {
    // this.filesToUpload = files.map(x => ({
    //   id: createObjectID(),
    //   file: x,
    //   name: x.name,
    //   expiration: "week" as "week"
    // }));
  }

  @action updateSelectedSortOption = async (opt: CustomerContractOption) => {
    this.selectedSortOption = opt;
    await this.fetch();
  };
}
